import React from 'react';
import { Box, Flex, Icon, SimpleGrid, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { MdPlayCircle, MdLocationOn, MdAccessTime } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import Card from '../../components/card/Card';
import MiniStatistics from '../../components/card/MiniStatistics';

interface ActivitySummary {
  playbackHours: number;
  activeLocations: number;
  recentEvents: Array<{ timestamp: string; action: string; details: string }>;
}

export function ActivitySection({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const iconBg = useColorModeValue('brand.500', 'brand.400');
  const eventIconBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-org-activity', organizationId],
    queryFn: () => apiClient.get<ActivitySummary>(`/admin/organizations/${organizationId}/activity`, { days: 30 }),
  });

  if (isLoading) return <Spinner size="sm" />;

  return (
    <Box data-testid="activity-section">
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" mb="20px">
        <MiniStatistics
          startContent={
            <Flex
              w="56px" h="56px"
              align="center" justify="center"
              borderRadius="50%"
              bg={iconBg}
            >
              <Icon as={MdPlayCircle} w="28px" h="28px" color="white" />
            </Flex>
          }
          name={t('admin.dashboard.playbackHours')}
          value={`${data?.playbackHours ?? 0}h`}
        />
        <MiniStatistics
          startContent={
            <Flex
              w="56px" h="56px"
              align="center" justify="center"
              borderRadius="50%"
              bg={iconBg}
            >
              <Icon as={MdLocationOn} w="28px" h="28px" color="white" />
            </Flex>
          }
          name={t('admin.dashboard.activeLocations')}
          value={data?.activeLocations ?? 0}
        />
      </SimpleGrid>

      <Card p="20px">
        <Text fontSize="lg" fontWeight="700" color={textColor} mb="16px">
          {t('admin.dashboard.recentActivity')}
        </Text>
        {data?.recentEvents?.length ? (
          data.recentEvents.map((event, i) => (
            <Flex
              key={i}
              align="center"
              py="12px"
              borderBottom={i < (data.recentEvents?.length ?? 0) - 1 ? '1px solid' : 'none'}
              borderColor={borderColor}
            >
              <Flex
                w="36px" h="36px"
                align="center" justify="center"
                borderRadius="50%"
                bg={eventIconBg}
                me="14px"
              >
                <Icon as={MdAccessTime} w="18px" h="18px" color={secondaryTextColor} />
              </Flex>
              <Box flex="1">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {event.action}
                </Text>
                <Text color={secondaryTextColor} fontSize="xs">
                  {event.details}
                </Text>
              </Box>
              <Text color={secondaryTextColor} fontSize="xs" whiteSpace="nowrap">
                {new Date(event.timestamp).toLocaleString()}
              </Text>
            </Flex>
          ))
        ) : (
          <Text color={secondaryTextColor} fontSize="sm">
            {t('admin.common.noResults')}
          </Text>
        )}
      </Card>
    </Box>
  );
}
